import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTour } from '@reactour/tour';
import type { LywTourStep } from './lywOnboardingTourSteps';

const POPOVER_SELECTOR = '.reactour__popover';
const STROKE = '#ffffff';
const STROKE_WIDTH = 2.4;
/** Pixels past the target bbox so the tip (and stroke) do not sit on / inside the highlight. */
const TIP_OUTSIDE_TARGET_PX = 20;

/** Popover rect must match this many consecutive frames before the arrow mounts. */
const POPOVER_STABLE_FRAMES = 5;
const POPOVER_RECT_EPS_PX = 0.8;
/** If the popover never stabilizes (layout thrash), show the arrow anyway. */
const POPOVER_SETTLE_TIMEOUT_MS = 800;

function rectsNearlyEqual(
  a: { l: number; t: number; w: number; h: number },
  b: { l: number; t: number; w: number; h: number },
): boolean {
  return (
    Math.abs(a.l - b.l) < POPOVER_RECT_EPS_PX &&
    Math.abs(a.t - b.t) < POPOVER_RECT_EPS_PX &&
    Math.abs(a.w - b.w) < POPOVER_RECT_EPS_PX &&
    Math.abs(a.h - b.h) < POPOVER_RECT_EPS_PX
  );
}

const POINTER_DRAW_STYLE = `
  @keyframes lywTourPointerDraw {
    to { stroke-dashoffset: 0; }
  }
  .lyw-tour-pointer-shaft {
    animation: lywTourPointerDraw 0.55s cubic-bezier(0.25, 0.46, 0.45, 0.98) forwards;
  }
  .lyw-tour-pointer-head {
    animation: lywTourPointerDraw 0.24s cubic-bezier(0.25, 0.46, 0.45, 0.98) 0.5s forwards;
  }
`;

function resolveTarget(step: LywTourStep | undefined): Element | null {
  if (!step) return null;
  const { selector } = step;
  if (typeof selector === 'string') {
    try {
      return document.querySelector(selector);
    } catch {
      return null;
    }
  }
  return selector ?? null;
}

/** Ray from (cx,cy) in direction (dx,dy) to first intersection with rect border. */
function rayToRectEdge(
  cx: number,
  cy: number,
  dx: number,
  dy: number,
  rect: DOMRect,
): { x: number; y: number } {
  if (dx === 0 && dy === 0) {
    return { x: cx, y: cy };
  }
  let t = Infinity;
  if (dx > 0) t = Math.min(t, (rect.right - cx) / dx);
  else if (dx < 0) t = Math.min(t, (rect.left - cx) / dx);
  if (dy > 0) t = Math.min(t, (rect.bottom - cy) / dy);
  else if (dy < 0) t = Math.min(t, (rect.top - cy) / dy);
  if (!Number.isFinite(t) || t < 0) {
    return { x: cx + dx * 0.01, y: cy + dy * 0.01 };
  }
  return { x: cx + dx * t, y: cy + dy * t };
}

function anchorOnPopoverToward(popRect: DOMRect, targetX: number, targetY: number) {
  const cx = popRect.left + popRect.width / 2;
  const cy = popRect.top + popRect.height / 2;
  const dx = targetX - cx;
  const dy = targetY - cy;
  return rayToRectEdge(cx, cy, dx, dy, popRect);
}

/** Tour step 0 (first step): arrow leaves from the bottom edge, 10% in from the left. */
function anchorPopoverStep0Start(popRect: DOMRect): { x: number; y: number } {
  return {
    x: popRect.left + popRect.width * 0.1,
    y: popRect.bottom,
  };
}

/** Tailwind `sm` is 640px; bottom nav uses `sm:hidden` (visible below that). */
function isMobileBottomNavViewport(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches;
}

/** Same ids as desktop `Sidebar` `data-tour` (final tour step is `reports`). */
const SIDEBAR_NAV_DATA_TOURS = new Set(['dashboard', 'profiles', 'my-plan', 'reports']);

function dataTourId(target: Element): string | null {
  const el =
    target instanceof HTMLElement && target.hasAttribute('data-tour')
      ? target
      : target.closest('[data-tour]');
  if (!(el instanceof HTMLElement)) return null;
  return el.getAttribute('data-tour');
}

/**
 * Where the arrow aims on the highlight and the outward normal for the withdrawn tip.
 * **Mobile** `currentStep` 0–4: step 0 → left edge mid; others → top center.
 * **Desktop** sidebar nav (`data-tour` in {@link SIDEBAR_NAV_DATA_TOURS}) **or** `currentStep` 0–4:
 * left/right vertical edge at mid-height toward the popover (so **Reports**, last step, matches Dashboard etc.).
 * Otherwise → edge from target center toward popover center.
 */
function targetAimPointAndOutward(
  targetRect: DOMRect,
  popRect: DOMRect,
  currentStep: number,
  target: Element,
): { ax: number; ay: number; nx: number; ny: number } {
  const inSteps1to5 = currentStep >= 0 && currentStep <= 4;
  const mobile = isMobileBottomNavViewport();
  const tourId = dataTourId(target);

  if (inSteps1to5 && mobile) {
    if (currentStep === 0) {
      return {
        ax: targetRect.left,
        ay: targetRect.top + targetRect.height / 2,
        nx: -1,
        ny: 0,
      };
    }
    return {
      ax: targetRect.left + targetRect.width / 2,
      ay: targetRect.top,
      nx: 0,
      ny: -1,
    };
  }

  const desktopSidebarLike =
    !mobile && (inSteps1to5 || (tourId != null && SIDEBAR_NAV_DATA_TOURS.has(tourId)));

  if (desktopSidebarLike) {
    const midY = targetRect.top + targetRect.height / 2;
    const popMidX = popRect.left + popRect.width / 2;
    const tgtMidX = targetRect.left + targetRect.width / 2;
    if (popMidX <= tgtMidX) {
      return { ax: targetRect.left, ay: midY, nx: -1, ny: 0 };
    }
    return { ax: targetRect.right, ay: midY, nx: 1, ny: 0 };
  }

  const tcx = targetRect.left + targetRect.width / 2;
  const tcy = targetRect.top + targetRect.height / 2;
  const pcx = popRect.left + popRect.width / 2;
  const pcy = popRect.top + popRect.height / 2;
  const edge = rayToRectEdge(tcx, tcy, pcx - tcx, pcy - tcy, targetRect);
  const elen = Math.hypot(edge.x - tcx, edge.y - tcy);
  if (elen > 1e-6) {
    return {
      ax: edge.x,
      ay: edge.y,
      nx: (edge.x - tcx) / elen,
      ny: (edge.y - tcy) / elen,
    };
  }
  return { ax: edge.x, ay: edge.y, nx: 0, ny: 1 };
}

function straightShaftD(x1: number, y1: number, x2: number, y2: number): string {
  return `M ${x1} ${y1} L ${x2} ${y2}`;
}

/**
 * Arc-like shaft for tour step 0 only. P2 is placed so the cubic’s tangent at the
 * end matches the segment from shaft end → tip (same line the arrow head uses), so
 * the join doesn’t look kinked.
 */
function naturalCurveShaftD(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  tipX: number,
  tipY: number,
): string {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const px = -uy;
  const py = ux;
  const bow = Math.min(Math.max(len * 0.22, 18), 150);
  const along = 0.42;
  const c1x = x1 + ux * len * along + px * bow;
  const c1y = y1 + uy * len * along + py * bow;

  const tvx = tipX - x2;
  const tvy = tipY - y2;
  const tvlen = Math.hypot(tvx, tvy) || 1;
  const tux = tvx / tvlen;
  const tuy = tvy / tvlen;
  const arm = Math.min(Math.max(tvlen * 0.55, 14), Math.max(len * 0.4, 50));
  const c2x = x2 - tux * arm;
  const c2y = y2 - tuy * arm;

  return `M ${x1} ${y1} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${x2} ${y2}`;
}

function sketchyArrowHeadD(
  tipX: number,
  tipY: number,
  fromX: number,
  fromY: number,
  size: number,
): string {
  const ang = Math.atan2(tipY - fromY, tipX - fromX);
  const back = ang + Math.PI;
  const spread = 0.5;
  const x1 = tipX + size * Math.cos(back - spread);
  const y1 = tipY + size * Math.sin(back - spread);
  const x2 = tipX + size * Math.cos(back + spread);
  const y2 = tipY + size * Math.sin(back + spread);
  return `M ${x1} ${y1} L ${tipX} ${tipY} L ${x2} ${y2}`;
}

function effectiveZIndex(el: HTMLElement): number {
  let node: HTMLElement | null = el;
  while (node) {
    const s = window.getComputedStyle(node);
    if (s.position !== 'static' && s.zIndex !== 'auto') {
      const z = Number.parseInt(s.zIndex, 10);
      if (Number.isFinite(z)) return z;
    }
    node = node.parentElement;
  }
  return 100_001;
}

type LineGeom = {
  shaft: string;
  head: string;
  zIndex: number;
};

/**
 * Full-viewport SVG: sketchy stroke from the tour popover edge to the highlighted
 * element (@reactour/tour does not provide this; `components.Arrow` is only the
 * next/prev chevrons).
 */
export function TourPopoverPointerOverlay() {
  const { isOpen, currentStep, steps } = useTour();
  const [geom, setGeom] = useState<LineGeom | null>(null);
  const [popoverSettled, setPopoverSettled] = useState(false);
  const settleSessionRef = useRef(0);
  const measureGeomRef = useRef<(() => void) | null>(null);

  useLayoutEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      setPopoverSettled(false);
      return;
    }

    const session = ++settleSessionRef.current;
    setPopoverSettled(false);

    let cancelled = false;
    let stableFrames = 0;
    let prev: { l: number; t: number; w: number; h: number } | null = null;
    let raf = 0;

    const finish = () => {
      if (cancelled || session !== settleSessionRef.current) return;
      measureGeomRef.current?.();
      requestAnimationFrame(() => {
        if (cancelled || session !== settleSessionRef.current) return;
        requestAnimationFrame(() => {
          if (cancelled || session !== settleSessionRef.current) return;
          setPopoverSettled(true);
        });
      });
    };

    const tick = () => {
      if (cancelled || session !== settleSessionRef.current) return;

      const pop = document.querySelector(POPOVER_SELECTOR) as HTMLElement | null;
      if (!pop || pop.getBoundingClientRect().width < 2) {
        stableFrames = 0;
        prev = null;
        raf = requestAnimationFrame(tick);
        return;
      }

      const r = pop.getBoundingClientRect();
      const snap = { l: r.left, t: r.top, w: r.width, h: r.height };

      if (prev && rectsNearlyEqual(prev, snap)) {
        stableFrames += 1;
      } else {
        stableFrames = 0;
      }
      prev = snap;

      if (stableFrames >= POPOVER_STABLE_FRAMES) {
        finish();
        return;
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);

    const timeoutId = window.setTimeout(() => finish(), POPOVER_SETTLE_TIMEOUT_MS);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      window.clearTimeout(timeoutId);
    };
  }, [isOpen, currentStep]);

  useLayoutEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      setGeom(null);
      return;
    }

    const step = steps[currentStep] as LywTourStep | undefined;
    if (step?.hidePopoverPointer) {
      setGeom(null);
      return;
    }

    const update = () => {
      const pop = document.querySelector(POPOVER_SELECTOR) as HTMLElement | null;
      const target = resolveTarget(step);
      if (!pop || !target) {
        setGeom(null);
        return;
      }

      const popRect = pop.getBoundingClientRect();
      const tgtRect = target.getBoundingClientRect();
      if (popRect.width < 2 || popRect.height < 2 || tgtRect.width < 1 || tgtRect.height < 1) {
        setGeom(null);
        return;
      }

      const aim = targetAimPointAndOutward(tgtRect, popRect, currentStep, target);

      const start =
        currentStep === 0
          ? anchorPopoverStep0Start(popRect)
          : anchorOnPopoverToward(popRect, aim.ax, aim.ay);

      const tipGap = TIP_OUTSIDE_TARGET_PX;
      const tip = {
        x: aim.ax + aim.nx * tipGap,
        y: aim.ay + aim.ny * tipGap,
      };

      const len = Math.hypot(tip.x - start.x, tip.y - start.y) || 1;
      const headSize = Math.min(18, len * 0.14 + 7);
      const inset = Math.min(10, len * 0.09);
      const ux = (tip.x - start.x) / len;
      const uy = (tip.y - start.y) / len;
      const shaftEnd = { x: tip.x - ux * inset, y: tip.y - uy * inset };

      const shaft =
        currentStep === 0
          ? naturalCurveShaftD(start.x, start.y, shaftEnd.x, shaftEnd.y, tip.x, tip.y)
          : straightShaftD(start.x, start.y, shaftEnd.x, shaftEnd.y);
      const head = sketchyArrowHeadD(tip.x, tip.y, shaftEnd.x, shaftEnd.y, headSize);

      const popZ = effectiveZIndex(pop);
      let zIndex = Math.max(1, popZ - 1);
      const mask = document.querySelector('.reactour__mask') as HTMLElement | null;
      if (mask) {
        const maskZ = effectiveZIndex(mask);
        if (zIndex <= maskZ) zIndex = maskZ + 1;
      }
      if (zIndex >= popZ) zIndex = Math.max(1, popZ - 1);

      setGeom({ shaft, head, zIndex });
    };

    measureGeomRef.current = update;

    update();
    const delayed = window.setTimeout(update, 120);
    const delayed2 = window.setTimeout(update, 400);

    const ro = new ResizeObserver(update);
    ro.observe(document.documentElement);
    const popEl = document.querySelector(POPOVER_SELECTOR);
    if (popEl) ro.observe(popEl);

    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);

    const target = resolveTarget(step);
    if (target instanceof HTMLElement) ro.observe(target);

    return () => {
      measureGeomRef.current = null;
      window.clearTimeout(delayed);
      window.clearTimeout(delayed2);
      ro.disconnect();
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen, currentStep, steps]);

  if (
    !isOpen ||
    typeof document === 'undefined' ||
    !geom ||
    !popoverSettled ||
    (steps[currentStep] as LywTourStep | undefined)?.hidePopoverPointer
  ) {
    return null;
  }

  const { innerWidth: w, innerHeight: h } = window;

  const svg = (
    <svg
      key={currentStep}
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      style={{
        position: 'fixed',
        left: 0,
        top: 0,
        pointerEvents: 'none',
        zIndex: geom.zIndex,
        overflow: 'visible',
      }}
      aria-hidden
    >
      <style type="text/css">{POINTER_DRAW_STYLE}</style>
      <path
        className="lyw-tour-pointer-shaft"
        d={geom.shaft}
        fill="none"
        pathLength={1}
        stroke={STROKE}
        strokeDasharray={1}
        strokeDashoffset={1}
        strokeWidth={STROKE_WIDTH}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        className="lyw-tour-pointer-head"
        d={geom.head}
        fill="none"
        pathLength={1}
        stroke={STROKE}
        strokeDasharray={1}
        strokeDashoffset={1}
        strokeWidth={STROKE_WIDTH}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  return createPortal(svg, document.body);
}
